import { atom } from 'jotai';
import type { Project, FileNode, FileContent } from '@shared/types';

// Current project
export const currentProjectAtom = atom<Project | null>(null);

// Open files (tabs)
export interface OpenFile extends FileContent {
  isDirty: boolean; // Has unsaved changes
}

export const openFilesAtom = atom<OpenFile[]>([]);

// Active file (currently displayed in editor)
export const activeFilePathAtom = atom<string | null>(null);

// File tree
export const fileTreeAtom = atom<FileNode[]>([]);

// Terminal output
export interface TerminalLine {
  id: string;
  type: 'input' | 'stdout' | 'stderr' | 'error';
  content: string;
  timestamp: number;
}

export const terminalLinesAtom = atom<TerminalLine[]>([]);

// Dev server status
export const devServerStatusAtom = atom<{
  status: 'stopped' | 'starting' | 'running' | 'error';
  port?: number;
  url?: string;
}>({ status: 'stopped' });

// Preview URL
export const previewUrlAtom = atom<string | null>((get) => {
  const devServer = get(devServerStatusAtom);
  return devServer.url || null;
});

// Helpers
export const addOpenFileAtom = atom(
  null,
  (get, set, file: FileContent) => {
    const openFiles = get(openFilesAtom);
    const exists = openFiles.find((f) => f.path === file.path);

    if (!exists) {
      set(openFilesAtom, [...openFiles, { ...file, isDirty: false }]);
    }

    set(activeFilePathAtom, file.path);
  }
);

export const closeFileAtom = atom(
  null,
  (get, set, path: string) => {
    const openFiles = get(openFilesAtom);
    const newFiles = openFiles.filter((f) => f.path !== path);
    set(openFilesAtom, newFiles);

    const activePath = get(activeFilePathAtom);
    if (activePath === path) {
      set(activeFilePathAtom, newFiles[0]?.path || null);
    }
  }
);

export const updateFileContentAtom = atom(
  null,
  (get, set, path: string, content: string) => {
    const openFiles = get(openFilesAtom);
    const newFiles = openFiles.map((f) =>
      f.path === path ? { ...f, content, isDirty: true } : f
    );
    set(openFilesAtom, newFiles);
  }
);

export const markFileSavedAtom = atom(
  null,
  (get, set, path: string) => {
    const openFiles = get(openFilesAtom);
    const newFiles = openFiles.map((f) =>
      f.path === path ? { ...f, isDirty: false } : f
    );
    set(openFilesAtom, newFiles);
  }
);

// Save file atom (for use with API)
export const saveFileAtom = atom(
  null,
  async (get, set, path: string) => {
    const openFiles = get(openFilesAtom);
    const file = openFiles.find((f) => f.path === path);
    if (!file) return { success: false, error: 'File not found' };

    const currentProject = get(currentProjectAtom);
    if (!currentProject) return { success: false, error: 'No project selected' };

    try {
      const relativePath = path.replace(currentProject.path + '/', '');

      // Import apiRequest dynamically to avoid circular dependency
      const { apiRequest } = await import('@/lib/api');

      await apiRequest('put', `/api/projects/${currentProject.id}/files/${relativePath}`, {
        content: file.content,
      });

      set(markFileSavedAtom, path);
      return { success: true };
    } catch (error) {
      console.error('Failed to save file:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to save' };
    }
  }
);
